import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import type { GiftPackageInput, GiftPackageResult, PackageTypeDto } from '@pokerroom/types';
import { api } from '../../../lib/api';
import {
  persistBusinessRequest,
  storedBusinessRequest,
} from '../../../hooks/useLocalBusinessConsent';
import { sessionVenueKey, useAuthStore } from '../../../store/auth.store';
import { financialErrorKey, useFinancialCapabilities } from './financialShared';
import { BUTTON, SMALL_BUTTON, INPUT, PANEL, Field } from './walletShared';

const authIdentity = () => {
  const s = useAuthStore.getState();
  return JSON.stringify([
    s.user?.id,
    sessionVenueKey(s.accessToken),
    s.sessionRevision,
    s.allowedActions,
  ]);
};

/** Gift recovery is scoped like financial recovery, without sale or local-consent admission. */
export function GiftPackagePanel({
  memberId,
  cap,
}: {
  memberId: string;
  cap: ReturnType<typeof useFinancialCapabilities>;
}) {
  const { t } = useTranslation('member');
  const key = `member-gift:${cap.identity}:GIFT`;
  const client = useQueryClient();
  const trigger = useRef<HTMLButtonElement>(null),
    live = useRef(true),
    inFlight = useRef(false);
  const signature = authIdentity();
  const current = () => live.current && signature === authIdentity();
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);
  const [saved] = useState(() => {
    try {
      const body = storedBusinessRequest<GiftPackageInput>(key)?.body;
      if (
        body &&
        (typeof body.operationKey !== 'string' ||
          !body.expectedTerms ||
          typeof body.note !== 'string')
      )
        throw Error('Invalid gift recovery');
      return { body: body ?? null, unreadable: false };
    } catch {
      return { body: null, unreadable: true };
    }
  });
  const [pending, setPending] = useState(saved.body),
    [show, setShow] = useState(!!saved.body);
  const [typeId, setTypeId] = useState(saved.body?.typeId ?? ''),
    [note, setNote] = useState(saved.body?.note ?? '');
  const [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  const [result, setResult] = useState<GiftPackageResult | null>(null);
  const types = useQuery({
    queryKey: ['package-types', cap.venue?.id],
    queryFn: () => api.get<PackageTypeDto[]>('/members/package-types'),
    enabled: !!cap.venue,
  });
  const type = types.data?.find((v) => v.id === typeId && v.isActive);
  const terms = pending?.expectedTerms ?? type;
  const locked = busy || !!pending || saved.unreadable;
  const canGift = cap.can('member:adjust_points') && !!cap.venue;
  async function submit() {
    if (
      inFlight.current ||
      !current() ||
      !canGift ||
      saved.unreadable ||
      (!pending && (!type || !note.trim()))
    )
      return;
    inFlight.current = true;
    const body: GiftPackageInput = pending ?? {
      typeId,
      note: note.trim(),
      operationKey: crypto.randomUUID(),
      expectedTerms: {
        name: type!.name,
        useCount: type!.useCount,
        validDays: type!.validDays,
        price: type!.price,
        covers: type!.covers,
        buyInAmounts: type!.buyInAmounts,
        allowRebuyAddon: type!.allowRebuyAddon,
      },
    };
    try {
      persistBusinessRequest(key, body);
    } catch {
      setError('financial.storageFailed');
      inFlight.current = false;
      return;
    }
    setPending(body);
    setBusy(true);
    setError('');
    setResult(null);
    try {
      const response = await api.post<GiftPackageResult>(
        `/members/${memberId}/packages/gift`,
        body,
      );
      if (!current()) return;
      sessionStorage.removeItem(key);
      setPending(null);
      setResult(response);
      setNote('');
      void client.invalidateQueries({ queryKey: ['member-packages', memberId] });
      void client.invalidateQueries({ queryKey: ['member-ledger-summary', memberId] });
    } catch (failure) {
      if (!current()) return;
      const data = isAxiosError(failure) ? failure.response?.data : undefined;
      // Only the first, matching request's certified rollback can unlock editing.
      if (
        !pending &&
        isAxiosError(failure) &&
        [400, 404, 409, 422].includes(failure.response?.status ?? 0) &&
        data?.protocolVersion === 1 &&
        data?.outcome === 'NOT_WRITTEN' &&
        data?.rejectionCode === 'BUSINESS_INPUT_REJECTED' &&
        [
          'GIFT_TERMS_CHANGED',
          'PASS_TYPE_NOT_FOUND',
          'BUSINESS_INPUT_REJECTED',
          'ASSOCIATION_MODE_REQUIRED',
        ].includes(data.code)
      ) {
        sessionStorage.removeItem(key);
        setPending(null);
        setError(financialErrorKey(failure, 'GIFT'));
        void types.refetch();
      } else
        setError(
          isAxiosError(failure) && failure.response?.status === 403
            ? 'financial.permissionRecovery'
            : 'financial.uncertain',
        );
    } finally {
      inFlight.current = false;
      if (current()) setBusy(false);
    }
  }
  const close = () => {
    setShow(false);
    setTypeId('');
    setNote('');
    setError('');
    trigger.current?.focus();
  };
  return (
    <section className={PANEL} aria-label={t('gift.title')}>
      {pending && !canGift && <p role="alert">{t('financial.permissionRecovery')}</p>}
      <button
        ref={trigger}
        type="button"
        className={SMALL_BUTTON}
        disabled={!canGift || busy}
        onClick={() => setShow(true)}
      >
        {t('gift.title')}
      </button>
      {show && (
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <h3 className="font-semibold">{t('gift.title')}</h3>
          <fieldset disabled={locked || !canGift} className="grid gap-3 sm:grid-cols-2">
            <Field label={t('walletType')}>
              <select
                aria-label={t('walletType')}
                className={INPUT}
                value={typeId}
                onChange={(e) => {
                  setTypeId(e.target.value);
                  setResult(null);
                }}
              >
                <option value="">{t('walletPickType')}</option>
                {types.data
                  ?.filter((v) => v.isActive)
                  .map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name}
                    </option>
                  ))}
              </select>
            </Field>
            <Field label={t('walletReason')}>
              <input
                aria-label={t('walletReason')}
                className={INPUT}
                maxLength={255}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </Field>
          </fieldset>
          {terms && (
            <p className="break-words">
              {t('gift.terms', {
                count: terms.useCount,
                validity:
                  terms.validDays === null
                    ? t('walletNoExpiry')
                    : t('gift.validDays', { days: terms.validDays }),
                price: terms.price,
              })}{' '}
              · {t(`walletCovers_${terms.covers}`)}
              {terms.buyInAmounts.length > 0 && ` · ${terms.buyInAmounts.join(', ')}`} ·{' '}
              {t(terms.allowRebuyAddon ? 'gift.rebuyAllowed' : 'gift.firstOnly')}
            </p>
          )}
          <p>{t('gift.noPayment')}</p>
          <div className="flex flex-wrap gap-2">
            <button
              type="submit"
              className={BUTTON}
              disabled={
                busy || !canGift || saved.unreadable || (!pending && (!type || !note.trim()))
              }
            >
              {t(busy ? 'gift.pending' : pending ? 'financial.retry' : 'gift.confirm')}
            </button>
            {!pending && (
              <button type="button" className={SMALL_BUTTON} disabled={busy} onClick={close}>
                {t('walletCancel')}
              </button>
            )}
          </div>
        </form>
      )}
      {(error || saved.unreadable) && (
        <p role="alert">{t(saved.unreadable ? 'financial.storageFailed' : error)}</p>
      )}
      {result && (
        <p role="status" className="break-all">
          {t('gift.completed', { code: result.code, price: result.priceSnapshot })}
        </p>
      )}
    </section>
  );
}
